import { useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiService } from "../../services/api";
import authService from "../../services/authService";
import OrderPDF from "../../components/order/OrderPDF";
import { printOrder } from "../../utils/printOrder";
import { ORDER_STATUS_TABS, OrderRow } from "../../components/order/OrdersGrid";

const AccountOrdersPage = () => {
  const [activeTab, setActiveTab] = useState("pendingDelivery");
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [selectedStoreId, setSelectedStoreId] = useState("all");
  const [page, setPage] = useState(1);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printOrderId, setPrintOrderId] = useState(null);
  const pageSize = 10;
  const printRef = useRef(null);

  // Get all stores the user has access to
  const stores = authService.getAvailableStores() || [];
  const clientIds = stores.map(s => s.id).filter(Boolean); // store.id is the clientId

  // Build query for fetching orders
  const buildOrdersQuery = useCallback((statusFilter) => {
    let filter = "";

    // Filter by store(s)
    if (selectedStoreId === "all") {
      // All stores - filter by all client IDs the user has access to
      if (clientIds.length > 0) {
        const clientIdList = clientIds.join(',');
        filter = `ClientID in (${clientIdList})`;
      }
    } else {
      // Specific store
      filter = `ClientID eq ${selectedStoreId}`;
    }

    if (statusFilter) {
      filter += filter ? ` and ${statusFilter}` : statusFilter;
    }

    return {
      $filter: filter || undefined,
      $orderby: "RequestedDeliveryDate desc",
      $expand: "Client,OptFulfillmentStatus,OptDeliveryStatus,OptPaymentStatus",
      $top: pageSize,
      $skip: (page - 1) * pageSize,
      $count: true
    };
  }, [clientIds, selectedStoreId, page, pageSize]);

  // Fetch orders using OrderTotals view
  const {
    data: ordersData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ["accountOrders", clientIds.join(','), selectedStoreId, activeTab, page],
    queryFn: async () => {
      if (clientIds.length === 0) return { orders: [], totalCount: 0 };

      const statusConfig = ORDER_STATUS_TABS[activeTab];
      const queryParams = buildOrdersQuery(statusConfig.filter);

      const response = await apiService.get("/storefront-odata/StorefrontOrderHistory", queryParams);

      return {
        orders: response.value || [],
        totalCount: response["@odata.count"] || 0
      };
    },
    enabled: clientIds.length > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Fetch order details when expanded
  const {
    data: orderDetails,
    isLoading: isLoadingDetails
  } = useQuery({
    queryKey: ["orderDetails", expandedOrder],
    queryFn: async () => {
      if (!expandedOrder) return null;

      const response = await apiService.get(`/storefront-odata/StorefrontOrders(${expandedOrder})`, {
        $expand: "Client($expand=Company($expand=State),State),OrderItems($expand=ProductSKUVariant($expand=ProductSKU($expand=Brand)),ProductBatchLot($expand=ProductBatch)),OptPaymentStatus,OptFulfillmentStatus,OptDeliveryStatus,ClientSalesRepUser"
      });

      return response;
    },
    enabled: !!expandedOrder,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const orders = ordersData?.orders || [];
  const totalCount = ordersData?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Toggle order expansion
  const toggleOrderExpand = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
    setExpandedOrder(null);
  };

  // Handle store filter change
  const handleStoreChange = (storeId) => {
    setSelectedStoreId(storeId);
    setPage(1);
    setExpandedOrder(null);
  };

  // Handle print order — OrderPDF fetches its own data via orderId
  const handlePrintOrder = useCallback((orderId) => {
    if (!orderId) return;
    setIsPrinting(true);
    setPrintOrderId(orderId);
  }, []);

  const handlePrintReady = useCallback(() => {
    if (printRef.current) {
      printOrder(printRef, { id: printOrderId });
    }
    setIsPrinting(false);
    setPrintOrderId(null);
  }, [printOrderId]);

  // No stores available
  if (stores.length === 0) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <div className="mb-4">
              <i className="bi bi-shop display-1 text-muted"></i>
            </div>
            <h2 className="mb-3">No Stores Available</h2>
            <p className="text-muted mb-4">
              You don't have access to any stores yet.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Page Header */}
      <div className="row mb-4">
        <div className="col">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h1 className="mb-1">All Orders</h1>
              <p className="text-muted mb-0">
                View orders across all your stores
              </p>
            </div>
            <Link to="/categories" className="btn btn-primary">
              <i className="bi bi-plus-lg me-2"></i>
              New Order
            </Link>
          </div>
        </div>
      </div>

      {/* Store Filter */}
      {stores.length > 1 && (
        <div className="row mb-4">
          <div className="col-md-4">
            <label className="form-label fw-semibold">
              <i className="bi bi-shop me-2"></i>
              Filter by Store
            </label>
            <select
              className="form-select"
              value={selectedStoreId}
              onChange={(e) => handleStoreChange(e.target.value)}
            >
              <option value="all">All Stores ({stores.length})</option>
              {stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Status Tabs */}
      <div className="row mb-4">
        <div className="col">
          <ul className="nav nav-tabs">
            {Object.entries(ORDER_STATUS_TABS).map(([key, config]) => (
              <li className="nav-item" key={key}>
                <button
                  className={`nav-link ${activeTab === key ? "active" : ""}`}
                  onClick={() => handleTabChange(key)}
                >
                  {config.label}
                  {activeTab === key && totalCount > 0 && (
                    <span className="badge bg-primary ms-2">{totalCount}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="alert alert-danger mb-4">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Failed to load orders. Please try again.
          <button className="btn btn-link p-0 ms-2" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading orders...</p>
        </div>
      ) : orders.length === 0 ? (
        /* Empty State */
        <div className="text-center py-5">
          <i className={`bi ${ORDER_STATUS_TABS[activeTab].emptyIcon} display-1 text-muted mb-3 d-block`}></i>
          <h4 className="text-muted mb-3">{ORDER_STATUS_TABS[activeTab].emptyMessage}</h4>
          <p className="text-muted mb-4">
            {activeTab === "pendingDelivery"
              ? "You don't have any pending deliveries right now."
              : "Start shopping to place your first order!"}
          </p>
          <Link to="/categories" className="btn btn-primary">
            Browse Products
          </Link>
        </div>
      ) : (
        /* Orders List */
        <>
          <div className="card border-0 shadow-sm">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "40px" }}></th>
                    <th>Order #</th>
                    {(selectedStoreId === "all" && stores.length > 1) && (
                      <th>Store</th>
                    )}
                    <th>Date Placed</th>
                    <th>Delivery Date</th>
                    <th className="text-center">Items</th>
                    <th className="text-end">Total</th>
                    <th className="text-center">Payment Status</th>
                    <th className="text-center">Delivery Status</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <OrderRow
                      key={order.orderID}
                      order={order}
                      isExpanded={expandedOrder === order.orderID}
                      onToggle={() => toggleOrderExpand(order.orderID)}
                      orderDetails={expandedOrder === order.orderID ? orderDetails : null}
                      isLoadingDetails={expandedOrder === order.orderID && isLoadingDetails}
                      showStore={selectedStoreId === "all" && stores.length > 1}
                      onPrint={handlePrintOrder}
                      isPrinting={isPrinting}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-4">
              <p className="text-muted mb-0">
                Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} of {totalCount} orders
              </p>
              <nav aria-label="Orders pagination">
                <ul className="pagination mb-0">
                  <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <i className="bi bi-chevron-left"></i>
                    </button>
                  </li>
                  {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = idx + 1;
                    } else if (page <= 3) {
                      pageNum = idx + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + idx;
                    } else {
                      pageNum = page - 2 + idx;
                    }
                    return (
                      <li key={pageNum} className={`page-item ${page === pageNum ? "active" : ""}`}>
                        <button
                          className="page-link"
                          onClick={() => setPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      </li>
                    );
                  })}
                  <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <i className="bi bi-chevron-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </>
      )}

      {/* Hidden OrderPDF for printing */}
      {printOrderId && (
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
          <OrderPDF ref={printRef} orderId={printOrderId} onReady={handlePrintReady} />
        </div>
      )}
    </div>
  );
};

export default AccountOrdersPage;
